import { formatCores } from '../../core/sizing'
import type { CatalogHostDto } from '../../data/types'

/*
 * There was a REFERENCE_SHAPE_LABEL here, captioning the catalog's price
 * column: the provider's competitive bid for the medium preset, not a
 * host-rental price. The column now renders the provider's own rate card
 * per resource — the price of what "Rent this host" actually opens — so the
 * caption and the bid it explained are both gone. `reference_price` remains
 * in the tenant API for its other consumers.
 */

export function formatMemory(bytes: number | null): string {
  if (!bytes) return 'Not reported'
  const gb = bytes / 1024 ** 3
  return `${gb >= 1 ? gb.toFixed(0) : (bytes / 1024 ** 2).toFixed(0)} ${gb >= 1 ? 'GB' : 'MB'}`
}

export function formatCpuCores(cpuMilli: number | null): string {
  if (cpuMilli === null || !Number.isFinite(cpuMilli)) return 'CPU not reported'
  return formatCores(cpuMilli)
}

function capacityAmount(bytes: number | null, label: string, suffix: string): string {
  const amount = formatMemory(bytes)
  return amount === 'Not reported' ? `${label} not reported` : `${amount} ${suffix}`
}

export function capacitySummary(
  capacity: CatalogHostDto['available'],
  includeGpu = true,
): string {
  if (!capacity) return 'Unavailable'
  const gpu = includeGpu && capacity.gpu ? ` · GPU ${capacity.gpu}` : ''
  return `${formatCpuCores(capacity.cpuMilli)} · ${capacityAmount(capacity.memoryBytes, 'RAM', 'RAM')} · ${capacityAmount(capacity.storageBytes, 'Storage', 'storage')}${gpu}`
}

function capacityPercentage(available: number | null | undefined, total: number | null | undefined): string | null {
  if (available === null || available === undefined || total === null || total === undefined || !Number.isFinite(available) || !Number.isFinite(total) || available < 0 || total < 0 || available > total) return null
  if (total === 0) return available === 0 ? '0%' : null
  const percentage = (available / total) * 100
  return `${percentage.toFixed(1).replace(/\.0$/, '')}%`
}

export interface AvailablePercentageValues {
  cpu: string
  memory: string
  storage: string
  gpu: string
}

/** Available capacity percentages, kept separate so each screen can choose its presentation. */
export function availablePercentageValues(host: CatalogHostDto): AvailablePercentageValues {
  if (host.observation_state === 'unavailable' || !host.available || !host.inventory) return { cpu: '—', memory: '—', storage: '—', gpu: '—' }

  return {
    cpu: capacityPercentage(host.available.cpuMilli, host.inventory.cpuMilli) ?? '—',
    memory: capacityPercentage(host.available.memoryBytes, host.inventory.memoryBytes) ?? '—',
    storage: capacityPercentage(host.available.storageBytes, host.inventory.storageBytes) ?? '—',
    gpu: capacityPercentage(host.available.gpuUnits, host.inventory.gpuUnits) ?? '—',
  }
}

export function hostLabel(host: CatalogHostDto) {
  return host.organization || 'Assigned host'
}

/** Door A's configure target, or Door B when no host was selected. */
export function rentalPathHref(owner?: string): string {
  return owner
    ? `/app/rent/configure?host=${encodeURIComponent(owner)}&path=host-available`
    : '/app/rent/configure?path=competitive'
}

/**
 * Why this host's figures cannot be trusted, or undefined.
 *
 * Attached to a rent action as a hint rather than used to DISABLE it. The
 * collector's observation is what the catalog knows; it is not what the
 * provider can serve. Gating the door on it let a single collector fault
 * remove the entire rental journey from the console — every host read "cannot
 * be rented until we hear from it again", and since the catalog row was the
 * only route to the chooser, all three rental paths became unreachable while
 * every provider was up and quoting.
 *
 * Letting the tenant through promises nothing. The chooser asks the provider
 * itself (`/rental/capability`, `/rental/offers`) and disables each direct
 * product on that live answer; a host product cannot be SIGNED without an
 * unexpired host offer from the provider. Neither fact comes from this
 * observation, and the competitive path needs no host at all.
 *
 * Keyed on `observation_state`, not on `rentable`. They are the same value
 * today (`tenant.py:366`) but they are different questions — this one is about
 * freshness, not permission — and the state has three answers where `rentable`
 * has two. `stale` and `unavailable` must not share a string: for a stale host
 * there ARE figures on screen and they may be old, while for an unavailable one
 * the availability and price cells both render `Unavailable`, so there is
 * nothing shown that could be out of date. "No capacity free" and "we do not
 * know" are different answers to a tenant.
 */
export function staleHostHint(host: CatalogHostDto): string | undefined {
  if (host.observation_state === 'fresh') return undefined
  return host.observation_state === 'stale'
    ? 'We last heard from this host a while ago, so the capacity and price shown may be out of date.'
    : 'We have not heard from this host, so we cannot show its capacity or price. You can still ask it for a price on the next screen.'
}

/** "Updated 3 minutes ago", from the console clock the observation was stored on. */
export function updatedAgo(collectedAt: number | null, now = Date.now() / 1000): string | null {
  if (collectedAt === null) return null
  const seconds = Math.max(0, Math.round(now - collectedAt))
  if (seconds < 60) return `Updated ${seconds} second${seconds === 1 ? '' : 's'} ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `Updated ${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Updated ${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `Updated ${days} day${days === 1 ? '' : 's'} ago`
}

export function trackRecordLabel(host: CatalogHostDto): string {
  // Thin evidence and no evidence both read as Unrated. Neither becomes a
  // percentage, because a percentage nothing supports is a claim.
  if (host.track_record.state !== 'rated' || host.track_record.score_percent === null) return 'Unrated'
  return `${host.track_record.score_percent.toFixed(1)}%`
}

export function verificationLabel(host: CatalogHostDto, auditDataAvailable: boolean): string {
  if (!auditDataAvailable) return 'Verification unavailable'
  // A provider nobody audited is not verified. "Unknown" would imply the
  // console failed to look, which is a different and more forgiving claim.
  return host.audited ? 'Verified' : 'Not verified'
}

export function locationObservationLabel(host: CatalogHostDto): string {
  const observation = host.location_observation
  if (!observation || observation.freshness === 'unavailable') return 'Location signals unavailable'
  if (observation.freshness === 'stale') return 'Stale location signals'
  if (observation.status === 'conflict') return 'Signals conflict'
  if (observation.status === 'corroborated') {
    return `Corroborated by ${observation.successful_probes} probes`
  }
  if (observation.status === 'observed') return 'Observed from console'
  return 'Unknown'
}

export function freshnessNote(host: CatalogHostDto): string | null {
  if (host.observation_state === 'fresh') return null
  return host.observation_state === 'stale' ? 'Stale data' : 'Unavailable'
}
